import { useState, type ReactNode } from 'react';
import {
  AcceptLoiDialog,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DecisionConfirmDialog,
  ExclusivityPanel,
  FUNNEL_STAGE_LABELS,
  HelpTip,
  LoiOutcomeChip,
  LoiReviewTable,
  SellerVotePanel,
  SellerVoteSummary,
  addDaysYmd,
  isAcceptancePending,
  responseCopy,
  todayYmd,
} from '@dataroom/shared';
import type {
  AdvanceFunnelRequest,
  ExclusivityTerms,
  ExclusivityView,
  FunnelStage,
  LoiDecision,
  LoiReviewItem,
} from '@dataroom/shared';

interface LoiManagementCardProps {
  lois: LoiReviewItem[];
  loading: boolean;
  error: string | null;
  /** Message from a failed response / advance (e.g. 409 exclusivity). */
  actionError: string | null;
  /** LOI id whose response is being recorded (disables its buttons). */
  decidingId: string | null;
  /** Actor id whose funnel is currently advancing (disables its controls). */
  advancingActorId: string | null;
  /** Record a response; an accept carries the exclusivity terms from the dialog. */
  onDecide: (loiId: string, decision: LoiDecision, exclusivity?: ExclusivityTerms) => void;
  /** Open the LOI's signed document read-only. */
  onOpen: (loi: LoiReviewItem) => void;
  onAdvance: (actorId: string, body: AdvanceFunnelRequest) => void;
  onDismissError: () => void;
  /**
   * Whether this admin console may ENTER LOI decisions at all (owner ruling
   * 2026-10-22): only in a room the intermediary runs, and even then each one is only
   * a proposal the owners must carry. In a room the buyer or the seller runs the
   * controls are not shown — the owners decide in the seller console.
   */
  adminMayDecide?: boolean;
  /** Per-room "owners may respond to LOIs" permission (migration 0019). */
  sellerCanRespondLoi: boolean;
  onToggleSellerLoiPermission: (next: boolean) => void;
  sellerLoiPending: boolean;
  sellerLoiError: string | null;
  /** ADR-0046 enforced exclusivity: the room's live exclusivity (admin view). */
  exclusivity: ExclusivityView | null;
  /** The room's IANA timezone — the accept dialog's "today". */
  timezone: string;
  onExtendExclusivity: (additionalDays: number) => void;
  onEndExclusivity: () => void;
  exclusivityPending: boolean;
  exclusivityError: string | null;
  /** Who sees the countdown besides the intermediary and the seller. */
  clockToBuyer: boolean;
  clockToOthers: boolean;
  onToggleClock: (setting: { clockToBuyer?: boolean; clockToOthers?: boolean }) => void;
  clockPending: boolean;
  clockError: string | null;
}

/** A labelled on/off switch matching the room-settings toggles. */
function SettingToggle({
  id,
  label,
  checked,
  pending,
  error,
  helpOn,
  helpOff,
  help,
  onToggle,
}: {
  id: string;
  label: string;
  checked: boolean;
  pending: boolean;
  error: string | null;
  helpOn: string;
  helpOff: string;
  /** The "?" beside the label. */
  help?: ReactNode;
  onToggle: (next: boolean) => void;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-1">
          <label htmlFor={id} className="text-sm font-medium">
            {label}
          </label>
          {help}
        </div>
        <button
          id={id}
          type="button"
          role="switch"
          aria-checked={checked}
          disabled={pending}
          onClick={() => onToggle(!checked)}
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${
            checked ? 'bg-primary' : 'bg-muted-foreground/40'
          }`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-surface shadow transition-transform ${
              checked ? 'translate-x-5' : 'translate-x-0.5'
            }`}
          />
        </button>
      </div>
      <p className="text-xs text-muted-foreground">{checked ? helpOn : helpOff}</p>
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

/** The next legal funnel step after a post-LOI stage, or null at the end. */
function nextStage(stage: FunnelStage): FunnelStage | null {
  switch (stage) {
    case 'loi_submitted':
      return 'due_diligence';
    case 'due_diligence':
      return 'exclusivity_expires';
    case 'exclusivity_expires':
      return 'closing';
    default:
      return null; // teaser..cim never appear here; closing is terminal
  }
}

/** Today (LOCAL calendar date) + n days as YYYY-MM-DD, for the 30/60/90/120
 * quick buttons — UTC "today" was already tomorrow on a US evening. */
function inDays(n: number): string {
  return addDaysYmd(todayYmd(), n);
}

/**
 * The per-buyer funnel advance control. For the dated steps
 * (exclusivity_expires / closing) it offers the 30/60/90/120-day quick buttons
 * plus a date picker (PRD-0004 §3h); the plain step (due_diligence) is a single
 * button. Terminal at `closing`.
 */
function AdvanceControl({
  loi,
  advancing,
  onAdvance,
}: {
  loi: LoiReviewItem;
  advancing: boolean;
  onAdvance: (actorId: string, body: AdvanceFunnelRequest) => void;
}) {
  const [pickDate, setPickDate] = useState('');
  const to = nextStage(loi.funnelStage);
  if (to === null) return <span className="text-xs text-muted-foreground">Closed</span>;
  // ADR-0046: an exclusivity is set by ACCEPTING the LOI and moved by an
  // extension — never by a hand-entered date (only legacy LOIs reach that step).
  if (to === 'exclusivity_expires' && loi.exclusivity) return null;

  const dated = to === 'exclusivity_expires' || to === 'closing';
  const label = FUNNEL_STAGE_LABELS[to];

  if (!dated) {
    return (
      <Button
        variant="outline"
        size="sm"
        disabled={advancing}
        onClick={() => onAdvance(loi.actorId, { to })}
      >
        {advancing ? 'Advancing…' : `Move to ${label}`}
      </Button>
    );
  }

  const quick = to === 'exclusivity_expires' ? [30, 60, 90, 120] : [];
  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium text-muted-foreground">Set {label} date</p>
      {quick.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {quick.map((n) => (
            <button
              key={n}
              type="button"
              disabled={advancing}
              onClick={() => onAdvance(loi.actorId, { to, date: inDays(n) })}
              className="rounded border px-2 py-0.5 text-xs font-medium hover:bg-muted disabled:opacity-50"
            >
              {n}d
            </button>
          ))}
        </div>
      ) : null}
      <div className="flex items-center gap-1">
        <input
          type="date"
          value={pickDate}
          disabled={advancing}
          onChange={(e) => setPickDate(e.target.value)}
          className="rounded border px-1.5 py-0.5 text-xs"
        />
        <Button
          variant="outline"
          size="sm"
          disabled={advancing || !pickDate}
          onClick={() => onAdvance(loi.actorId, { to, date: pickDate })}
        >
          {advancing ? '…' : 'Set'}
        </Button>
      </div>
    </div>
  );
}

/**
 * PRD-0004 §3h LOI management (ADR-0029) — every LOI a buyer uploaded, with the
 * signed document opened read-only and Accept / Counter / Reject recorded as a
 * status. Whatever the intermediary enters is only a PROPOSAL (owner ruling
 * 2026-10-22): counter and reject ask "Are you sure?", and ACCEPT opens the
 * enforced-exclusivity dialog (ADR-0046) — a plain warning of the consequences plus
 * the required period (30/60/90/120 days or custom) and start date — and then
 * waits for the owners' vote. In a room the buyer or the seller runs there are no
 * decision controls at all (`adminMayDecide` false). The live exclusivity shows
 * above the table (countdown, extend, end), and the LOI settings hold the
 * countdown-visibility toggles.
 * Presentational: the page owns the mutations. Server-side refusals (another
 * exclusivity live, already decided) surface as the action error.
 */
export function LoiManagementCard({
  lois,
  loading,
  error,
  actionError,
  decidingId,
  advancingActorId,
  onDecide,
  onOpen,
  onAdvance,
  onDismissError,
  adminMayDecide = true,
  sellerCanRespondLoi,
  onToggleSellerLoiPermission,
  sellerLoiPending,
  sellerLoiError,
  exclusivity,
  timezone,
  onExtendExclusivity,
  onEndExclusivity,
  exclusivityPending,
  exclusivityError,
  clockToBuyer,
  clockToOthers,
  onToggleClock,
  clockPending,
  clockError,
}: LoiManagementCardProps) {
  // The LOI whose Accept dialog is open.
  const [accepting, setAccepting] = useState<LoiReviewItem | null>(null);
  // The counter / decline waiting for its "Are you sure?".
  const [responding, setResponding] = useState<{
    loi: LoiReviewItem;
    decision: 'counter' | 'reject';
  } | null>(null);
  const buyerOf = (l: LoiReviewItem) => l.actorName?.trim() || l.actorEmail || 'this buyer';
  // Open votes, read-only (the admin never votes). The exclusivity panel above
  // already shows the vote on a pending acceptance or extension.
  const shownAbove = exclusivity?.sellerVote ?? null;
  const votePanels = lois.flatMap((l) =>
    l.sellerVote &&
    !(
      shownAbove &&
      shownAbove.kind === l.sellerVote.kind &&
      shownAbove.subjectId === l.sellerVote.subjectId
    )
      ? [l.sellerVote]
      : [],
  );
  return (
    <Card>
      <CardHeader>
        <CardTitle>LOI management</CardTitle>
        <p className="text-sm text-muted-foreground">
          {adminMayDecide
            ? 'Review the signed LOIs buyers upload, propose a response (accept, counter or reject), and set the closing date. Only the owners decide: whatever you enter is a proposal that waits for owners holding more than 50% of the company to approve it. Accepting starts an exclusivity period that pauses every other buyer’s access until closing or the period ends.'
            : 'Review the signed LOIs buyers upload and set the closing date. In this room the owners decide letters of intent, in the seller console — there are no accept, counter or reject controls here.'}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* The LOI setting lives here rather than in the general room Config panel. */}
        <div className="space-y-4 rounded-md border bg-muted/30 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            LOI settings
          </p>
          {adminMayDecide ? (
            <SettingToggle
              id="seller-can-respond-loi"
              label="Let the owners respond to LOIs"
              checked={sellerCanRespondLoi}
              pending={sellerLoiPending}
              error={sellerLoiError}
              helpOn="On — the owners can accept, counter, or reject an LOI themselves (decided by a majority of ownership). You can always propose a response too; the owners always vote on it."
              helpOff="Off — only you (the intermediary) can propose a response to LOIs; the owners still vote on every one."
              help={<HelpTip id="admin.loi.respond-toggle" className="ml-1" />}
              onToggle={onToggleSellerLoiPermission}
            />
          ) : null}
          <SettingToggle
            id="exclusivity-clock-to-buyer"
            label="Show the exclusivity countdown to the accepted buyer"
            checked={clockToBuyer}
            pending={clockPending}
            error={null}
            helpOn="On — the buyer holding exclusivity sees how long it has left."
            helpOff="Off — only you and the owners see the countdown."
            help={<HelpTip id="admin.loi.clock-toggles" className="ml-1" />}
            onToggle={(next) => onToggleClock({ clockToBuyer: next })}
          />
          <SettingToggle
            id="exclusivity-clock-to-others"
            label="Show the countdown to the other (paused) buyers"
            checked={clockToOthers}
            pending={clockPending}
            error={clockError}
            helpOn="On — paused buyers see when the exclusivity period ends."
            helpOff="Off — paused buyers are told access is paused, without a date."
            help={<HelpTip id="admin.loi.clock-toggles" className="ml-1" />}
            onToggle={(next) => onToggleClock({ clockToOthers: next })}
          />
        </div>

        {exclusivity ? (
          <ExclusivityPanel
            view={exclusivity}
            viewer="intermediary"
            canExtend={adminMayDecide}
            pending={exclusivityPending}
            error={exclusivityError}
            onExtend={onExtendExclusivity}
            onEnd={onEndExclusivity}
          />
        ) : null}

        {actionError ? (
          <div
            role="alert"
            className="flex items-start justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
          >
            <span>{actionError}</span>
            <button
              type="button"
              onClick={onDismissError}
              className="text-xs font-medium underline"
            >
              Dismiss
            </button>
          </div>
        ) : null}

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : lois.length > 0 ? (
          <LoiReviewTable
            lois={lois}
            canRespond={adminMayDecide}
            decidingId={decidingId}
            onDecide={(loiId, decision) => {
              const loi = lois.find((l) => l.id === loiId) ?? null;
              if (!loi) return;
              if (decision === 'accept') setAccepting(loi);
              else setResponding({ loi, decision });
            }}
            onOpen={onOpen}
            statusAside={(loi) =>
              isAcceptancePending(loi) ? <HelpTip id="admin.loi.pending-vote" /> : null
            }
            renderExtra={(loi) => (
              <div className="flex flex-col items-end gap-1.5" data-testid={`loi-extra-${loi.id}`}>
                {loi.outcome ? (
                  <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                    Outcome <LoiOutcomeChip outcome={loi.outcome} />
                  </span>
                ) : null}
                {loi.sellerVote ? (
                  <span className="text-xs text-muted-foreground">
                    Owners’ vote: <SellerVoteSummary decision={loi.sellerVote} />
                  </span>
                ) : null}
                {loi.status === 'accepted' || loi.status === 'accepted_signed' ? (
                  <AdvanceControl
                    loi={loi}
                    advancing={advancingActorId === loi.actorId}
                    onAdvance={onAdvance}
                  />
                ) : null}
              </div>
            )}
          />
        ) : (
          <p className="text-sm text-muted-foreground">
            No LOIs submitted yet. They appear here once a buyer uploads a signed LOI from the
            portal.
          </p>
        )}
        {votePanels.length > 0 ? (
          <div className="space-y-3" data-testid="loi-seller-votes">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Waiting for the owners’ vote
            </p>
            {votePanels.map((d) => (
              <SellerVotePanel key={`${d.kind}-${d.subjectId}`} decision={d} />
            ))}
          </div>
        ) : null}
        {responding ? (
          <DecisionConfirmDialog
            copy={responseCopy(responding.decision, buyerOf(responding.loi), 'intermediary')}
            danger={responding.decision === 'reject'}
            pending={decidingId === responding.loi.id}
            onCancel={() => setResponding(null)}
            onConfirm={() => {
              onDecide(responding.loi.id, responding.decision);
              setResponding(null);
            }}
          />
        ) : null}
        {accepting && adminMayDecide ? (
          <AcceptLoiDialog
            buyerLabel={buyerOf(accepting)}
            team={accepting.team ?? []}
            teamSize={accepting.teamSize ?? accepting.team?.length ?? 0}
            timezone={timezone}
            sellerValidationRequired
            viewer="intermediary"
            legendAside={<HelpTip id="admin.loi.exclusivity-period" />}
            onClose={() => setAccepting(null)}
            onConfirm={(terms) => {
              onDecide(accepting.id, 'accept', terms);
              setAccepting(null);
            }}
          />
        ) : null}
      </CardContent>
    </Card>
  );
}
